import httpClient from "@config/axios";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import type { GradeConfigResponse } from "@vTypes/gradeConfig";
import {
  buildGradeSelectOptionsFromGradeNames,
  extractGradeSlotKeysFromGradeConfig,
  parseShiftSettings,
  type GradeShiftCounts,
} from "@utils/grade-util";
import { useQuery } from "@tanstack/react-query";

const CACHING_TIME = 5 * 60 * 1000;

/** GET 후 `constraints`만 `parseShiftSettings`로 변환한 형태 */
export type GradeConfigQueryResult = Omit<
  GradeConfigResponse,
  "constraints"
> & {
  constraints: Record<string, GradeShiftCounts>;
  /**
   * 원본 `constraints`에서 복원한 등급 번호 슬롯 (예: `["1","2","7"]`).
   * `parseShiftSettings`와 무관하게 D/E/N 등에서 추출.
   */
  gradeSlotKeys: string[];
  /** 드롭다운용 — API `grade_names` 키·값만 사용 */
  gradeSelectOptions: Array<{ value: string; label: string }>;
};

export default function useGradeConfig({
  selectedGroupId,
}: {
  selectedGroupId: string | null;
}) {
  const groupId = selectedGroupId ?? "";

  return useQuery<GradeConfigResponse, Error, GradeConfigQueryResult>({
    queryKey: QUERY_KEYS.grade.config(groupId),
    queryFn: () =>
      httpClient.get<GradeConfigResponse>("/grade/config", {
        params: groupId ? { group_id: groupId } : undefined,
      }),
    staleTime: CACHING_TIME,
    select: (res) => {
      const parsedConstraints = parseShiftSettings(res.constraints);
      const gradeSlotKeys = extractGradeSlotKeysFromGradeConfig(res);
      const gradeSelectOptions = buildGradeSelectOptionsFromGradeNames(
        res.grade_names
      );
      return {
        ...res,
        constraints: parsedConstraints,
        gradeSlotKeys,
        gradeSelectOptions,
      };
    },
  });
}
